import {Component, DestroyRef, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DxChartModule, DxSelectBoxModule} from 'devextreme-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    GetPreProductionOrdersScoreQuery,
    GetPreProductionOrdersTrendingQuery,
    MyScoreTrendingGrouping,
    OrdersClient,
    PreProductionOrdersScoreDto,
} from '@services';
import {DoughnutChartTableComponent, DoughnutChartTableSlice} from '../../../../components/library/doughnut-chart-table/doughnut-chart-table.component';
import {OutboundMyScoreFilterBarComponent, OutboundMyScoreFilters} from '../outbound-my-score-filter-bar/outbound-my-score-filter-bar.component';
import {CardItem} from '../../../attendance/my-score/summary-card/summary-card.component';

type TrendingPoint = { period: Date; released: number; late: number };

export function preProductionOrdersPalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
    return {
        ['PO Received']: getColor('--bs-secondary'),
        ['Planning']: getColor('--bs-info'),
        ['Released to HDC']: getColor('--bs-success'),
        ['Late Released to HDC']: getColor('--bs-warning'),
        ['Rejection & No Stock']: getColor('--bs-danger'),
    };
}

export function preProductionSummaryTiles(dto: PreProductionOrdersScoreDto): CardItem[] {
    return [
        {icon: 'fas fa-arrow-right-to-bracket', color: 'text-info', bg: 'bg-info-subtle', value: dto.newPoReceived ?? 0, label: 'New PO Received'},
        {icon: 'fas fa-circle-check', color: 'text-success', bg: 'bg-success-subtle', value: dto.posReleasedToHdc ?? 0, label: 'POs Released to HDC'},
        {icon: 'fas fa-circle-check', color: 'text-warning', bg: 'bg-warning-subtle', value: dto.loadsReleasedToHdc ?? 0, label: 'Loads Released to HDC'},
        {icon: 'fas fa-cube', color: 'text-info', bg: 'bg-info-subtle', value: dto.poUnitsReceived ?? 0, label: 'PO Units Received'},
        {icon: 'fas fa-cube', color: 'text-success', bg: 'bg-success-subtle', value: dto.poUnitsReleasedToHdc ?? 0, label: 'PO Units Released to HDC'},
        {icon: 'fas fa-cube', color: 'text-warning', bg: 'bg-warning-subtle', value: dto.poUnitsRejected ?? 0, label: 'PO Units Rejected'},
    ];
}

@Component({
    selector: 'app-pre-production-orders',
    imports: [CommonModule, DoughnutChartTableComponent, OutboundMyScoreFilterBarComponent, DxChartModule, DxSelectBoxModule],
    templateUrl: './pre-production-orders.component.html',
})
export class PreProductionOrdersComponent {
    private ordersClient = inject(OrdersClient);
    private destroyRef = inject(DestroyRef);

    protected readonly PoReceived = 'PO Received';
    protected readonly Planning = 'Planning';
    protected readonly ReleasedToHdc = 'Released to HDC';
    protected readonly LateReleasedToHdc = 'Late Released to HDC';
    protected readonly RejectionAndNoStock = 'Rejection & No Stock';
    private readonly buckets = [this.PoReceived, this.Planning, this.ReleasedToHdc, this.LateReleasedToHdc, this.RejectionAndNoStock];

    protected palette: Record<string, string> = {};
    protected releasedColor = '';
    protected lateColor = '';

    protected grouping = MyScoreTrendingGrouping.Day;
    protected groupingOptions = [
        {value: MyScoreTrendingGrouping.Day, text: 'Day'},
        {value: MyScoreTrendingGrouping.Hour, text: 'Hour'},
    ];

    protected slices: DoughnutChartTableSlice[] = [];
    protected myScorePercent: number | null = null;
    protected summaryTiles: CardItem[] = [];
    protected trendingData: TrendingPoint[] = [];

    private filters?: OutboundMyScoreFilters;

    constructor() {
        this.palette = preProductionOrdersPalette();
        this.releasedColor = this.palette[this.ReleasedToHdc];
        this.lateColor = this.palette[this.LateReleasedToHdc];
    }

    protected onFiltersChanged(filters: OutboundMyScoreFilters): void {
        this.filters = filters;
        this.loadScore();
        this.loadTrending();
    }

    protected onGroupingChanged(): void {
        this.loadTrending();
    }

    protected customizeTrendingTooltip = (info: { seriesName: string; value: number }) => ({
        text: `${info.seriesName}: ${info.value}%`,
    });

    protected percentAxisLabel = (info: { value: number }) => `${info.value}%`;

    private loadScore(): void {
        if (!this.filters) return;

        this.ordersClient
            .getPreProductionOrdersScore(new GetPreProductionOrdersScoreQuery(this.filters))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                const totals = dto.totals ?? {};
                this.slices = this.buckets.map(key => ({argument: key, value: totals[key] ?? 0}));
                this.myScorePercent = dto.myScorePercent ?? null;
                this.summaryTiles = preProductionSummaryTiles(dto);
            });
    }

    private loadTrending(): void {
        if (!this.filters) return;

        this.ordersClient
            .getPreProductionOrdersTrending(new GetPreProductionOrdersTrendingQuery({...this.filters, grouping: this.grouping}))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                this.trendingData = (dto.points ?? []).map(p => ({
                    period: p.period!,
                    released: p.releasedPercent ?? 0,
                    late: p.latePercent ?? 0,
                }));
            });
    }
}
